import { useState, useCallback } from 'react';
import { ArrowRight, ArrowDown } from 'lucide-react';
import { SlashPill } from './SlashPill';
import { CyclingPlaceholder } from './CyclingPlaceholder';

interface CommandBarProps {
  inputRef: React.RefObject<HTMLInputElement | null>;
  placeholders: string[];
  expanded: boolean;
  sizeScale?: number;
  disabled?: boolean;
  hasMessages?: boolean;
  staticPlaceholder?: string | null;
  externalSlashCommand?: 'create' | 'revise' | null;
  alwaysShowSend?: boolean;
  onSubmit: (text: string, slashCommand: 'create' | 'revise' | null) => void;
  onDismiss: () => void;
  onFocus?: () => void;
  onBlur?: () => void;
}

const SLASH_PLACEHOLDERS: Record<string, string[]> = {
  create: ['Describe what you want to learn...'],
  revise: ['What would you like to revise?'],
};

export function CommandBar({
  inputRef,
  placeholders,
  expanded,
  sizeScale = 1,
  disabled,
  onSubmit,
  onDismiss,
  onFocus,
  onBlur,
  hasMessages,
  staticPlaceholder,
  externalSlashCommand,
  alwaysShowSend,
}: CommandBarProps) {
  const uiScale = Math.min(Math.max(sizeScale, 0.92), 1.35);
  const barHeight = Math.round(44 * uiScale);
  const horizontalPadding = Math.round(18 * uiScale);
  const inputFontSize = +(15 * uiScale).toFixed(2);
  const shortcutFontSize = +(11 * uiScale).toFixed(2);
  const shortcutPadY = Math.max(2, Math.round(2 * uiScale));
  const shortcutPadX = Math.max(6, Math.round(6 * uiScale));
  const sendSize = Math.round(28 * uiScale);
  const sendIconSize = Math.round(14 * uiScale);

  const [query, setQuery] = useState('');
  const [slashCommand, setSlashCommand] = useState<'create' | 'revise' | null>(null);

  // Allow external mode selection (e.g. from action boxes)
  const [prevExternal, setPrevExternal] = useState(externalSlashCommand);
  if (externalSlashCommand !== prevExternal) {
    setPrevExternal(externalSlashCommand);
    if (externalSlashCommand) {
      setSlashCommand(externalSlashCommand);
      setQuery('');
    }
  }

  // Reset internal state when the bar collapses (covers overlay click + Escape)
  const [wasExpanded, setWasExpanded] = useState(false);
  if (expanded && !wasExpanded) setWasExpanded(true);
  if (!expanded && wasExpanded) {
    setWasExpanded(false);
    setSlashCommand(null);
    setQuery('');
  }

  const handleChange = useCallback((value: string) => {
    if (/^\/create(\s|$)/i.test(value)) {
      setSlashCommand('create');
      setQuery(value.replace(/^\/create\s*/i, ''));
      return;
    }
    if (/^\/revise(\s|$)/i.test(value)) {
      setSlashCommand('revise');
      setQuery(value.replace(/^\/revise\s*/i, ''));
      return;
    }
    setQuery(value);
  }, []);

  const clearSlash = useCallback(() => {
    setSlashCommand(null);
    setQuery('');
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        if (expanded) {
          setSlashCommand(null);
          setQuery('');
          onDismiss();
          return;
        }
        if (slashCommand) {
          clearSlash();
          return;
        }
        inputRef.current?.blur();
        return;
      }
      if (e.key === 'Backspace' && query === '' && slashCommand) {
        e.preventDefault();
        clearSlash();
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        const trimmed = query.trim();
        if (!trimmed || disabled) return;
        onSubmit(trimmed, slashCommand);
        setQuery('');
      }
    },
    [query, slashCommand, expanded, disabled, onSubmit, onDismiss, clearSlash, inputRef]
  );

  const displayPlaceholders = slashCommand ? SLASH_PLACEHOLDERS[slashCommand] : placeholders;

  const [hovered, setHovered] = useState(false);

  return (
    <div
      className="flex w-full items-center gap-2"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: expanded
          ? 'var(--shell-command-bg-active)'
          : hovered
            ? 'var(--shell-command-bg-hover)'
            : 'var(--shell-command-bg)',
        border: `1px solid ${
          expanded
            ? 'var(--shell-command-border-active)'
            : hovered
              ? 'var(--shell-command-border-hover)'
              : 'var(--shell-command-border-strong)'
        }`,
        borderRadius: 9999,
        padding: `0 ${horizontalPadding}px`,
        height: barHeight,
        backdropFilter: 'blur(12px) saturate(1.2)',
        WebkitBackdropFilter: 'blur(12px) saturate(1.2)',
        boxShadow: expanded
          ? 'inset 0 0.5px 0 var(--shell-glass-highlight), 0 2px 6px rgba(0,0,0,0.07)'
          : hovered
            ? 'inset 0 0.5px 0 var(--shell-glass-highlight), 0 2px 6px rgba(0,0,0,0.07)'
            : 'inset 0 0.5px 0 var(--shell-glass-highlight), 0 1px 3px rgba(0,0,0,0.05)',
        transition: 'border-color 0.15s, box-shadow 0.15s, background-color 0.15s',
      }}
    >
      {slashCommand && <SlashPill command={slashCommand} onClear={clearSlash} />}
      <div className="relative min-w-0 flex-1">
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => handleChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={onFocus}
          onBlur={onBlur}
          className="w-full bg-transparent outline-none"
          style={{
            fontSize: inputFontSize,
            fontWeight: 450,
            color: 'var(--color-text)',
            fontFamily: 'var(--font-sans)',
          }}
          placeholder={hasMessages ? staticPlaceholder || 'Type a message...' : undefined}
          autoComplete="off"
          spellCheck={false}
          aria-label="AI command bar"
        />
        {!query && !hasMessages && (
          <CyclingPlaceholder items={displayPlaceholders} fontSize={inputFontSize} />
        )}
      </div>
      {!expanded && !query && (
        <kbd
          className="hidden shrink-0 learner-double:inline"
          style={{
            fontSize: shortcutFontSize,
            fontWeight: 500,
            fontFamily: 'inherit',
            color: 'var(--shell-shortcut-text)',
            background: 'var(--shell-shortcut-bg)',
            border: '1px solid var(--shell-shortcut-border)',
            borderRadius: 5,
            padding: `${shortcutPadY}px ${shortcutPadX}px`,
            lineHeight: 1,
          }}
        >
          ⌘ K
        </kbd>
      )}
      {expanded && (
        <button
          type="button"
          onClick={() => {
            const trimmed = query.trim();
            if (!trimmed || disabled) return;
            onSubmit(trimmed, slashCommand);
            setQuery('');
          }}
          disabled={disabled || !query.trim()}
          className="flex shrink-0 items-center justify-center rounded-full hover:scale-105 active:scale-95"
          style={{
            width: sendSize,
            height: sendSize,
            background: 'var(--color-accent)',
            color: 'var(--color-text-on-accent)',
            opacity: query.trim() ? 1 : alwaysShowSend ? 0.4 : 0,
            scale: query.trim() || alwaysShowSend ? '1' : '0.8',
            pointerEvents: query.trim() ? 'auto' : 'none',
            transition: 'opacity 0.2s ease, scale 0.2s ease',
          }}
          aria-label="Send"
        >
          {hasMessages ? (
            <ArrowDown size={sendIconSize} strokeWidth={2.5} />
          ) : (
            <ArrowRight size={sendIconSize} strokeWidth={2.5} />
          )}
        </button>
      )}
    </div>
  );
}
